import { useEffect, useState } from "react";
import { localStorageProvider } from "../../AppController";
import { useAppSelector } from "../../hooks/redux";
import { isPlaceholderName } from "../../util/placeholderName";
import { liveOnboardingController } from "./controllers";
import { ONBOARDING_PENDING_KEY } from "./onboardingKeys";

// Re-exported for existing importers (e.g. SignUp/useHook) that pull the key
// from the gate. The canonical definition lives in ./onboardingKeys so lighter
// modules can import it without the AppController/Amplify graph.
export { ONBOARDING_PENDING_KEY };
const ONBOARDING_COMPLETED_EVENT = "cip:onboarding-completed";

const normalizedIdentity = (value?: string | null): string =>
  (value ?? "").trim().toLowerCase();

const isPendingForUser = (
  pendingIdentity: string | null,
  userEmail?: string | null
): boolean => {
  const pending = normalizedIdentity(pendingIdentity);
  return (
    pending !== "" &&
    pending !== "true" &&
    pending === normalizedIdentity(userEmail)
  );
};

export const clearOnboardingPendingFor = (userEmail?: string | null) => {
  const pendingIdentity = localStorageProvider.get(ONBOARDING_PENDING_KEY);
  if (
    pendingIdentity === "true" ||
    isPendingForUser(pendingIdentity, userEmail)
  ) {
    localStorageProvider.remove(ONBOARDING_PENDING_KEY);
  }
};

export const notifyOnboardingCompleted = () => {
  window.dispatchEvent(new Event(ONBOARDING_COMPLETED_EVENT));
};

// Per-user "already onboarded" cache. Onboarding now applies to EVERY
// authenticated user, so without a cache every returning user would pay a
// blocking /onboarding/me round-trip (and a loader flash) on every app boot.
// Once the backend confirms completion we remember it per identity and let that
// user skip straight to the app. The backend stays the source of truth: this is
// only a fast-path, and it is only ever set to "true" (completion is permanent).
const COMPLETED_CACHE_PREFIX = "onboardingComplete:";
const completedCacheKey = (identity: string) =>
  `${COMPLETED_CACHE_PREFIX}${identity}`;

const isOnboardingCompletedCached = (userEmail?: string | null): boolean => {
  const identity = normalizedIdentity(userEmail);
  return (
    identity !== "" &&
    localStorageProvider.get(completedCacheKey(identity)) === "true"
  );
};

/** Remember (per user) that onboarding is finished so future boots skip it. */
export const markOnboardingCompletedLocally = (userEmail?: string | null) => {
  const identity = normalizedIdentity(userEmail);
  if (identity) localStorageProvider.set(completedCacheKey(identity), "true");
};

const isOnboardingCandidate = (user?: User): boolean => {
  const pendingIdentity = localStorageProvider.get(ONBOARDING_PENDING_KEY);
  const placeholder = isPlaceholderName(user?.firstName, user?.lastName);
  // Legacy builds stored "true". Honor it only for placeholder accounts so an
  // unrelated existing account cannot inherit the browser-wide flag.
  const legacyPending = pendingIdentity === "true" && placeholder;
  return (
    legacyPending ||
    placeholder ||
    isPendingForUser(pendingIdentity, user?.email)
  );
};

/**
 * Tri-state result:
 *  - "checking": a candidate is being verified against the backend (show a loader)
 *  - "onboard":  the user must go through onboarding before the app
 *  - "app":      the user is cleared for the normal app
 */
export type OnboardingGateState = "checking" | "onboard" | "app";
type OnboardingGateSnapshot = {
  candidateKey: string | null;
  state: OnboardingGateState;
};

/**
 * Decides whether a freshly-authenticated user must be routed into onboarding.
 *
 * Onboarding is a universal gate: EVERY authenticated user is verified against
 * the backend (`/onboarding/me`), and anyone whose status is not `completed` is
 * routed through the flow before the app.
 *
 * Design goals:
 *  - Universal: no local flag or placeholder heuristic is required to be caught;
 *    the backend status is the authority. A per-user "completed" cache is the
 *    only local state, and purely a performance fast-path (returning, onboarded
 *    users skip the round-trip and the loader).
 *  - No redirect loops: `status === "completed"` is the authoritative off-switch.
 *  - Fail open by default: if the status request errors we send the user to the
 *    app rather than trapping the whole user base during a backend outage — the
 *    check simply re-runs on the next load. The one exception is a strongly
 *    "flagged" brand-new user (signup/invite pending flag or placeholder name),
 *    where we fail closed so a transient error can't skip their required setup.
 */
export const useOnboardingGate = (enabled: boolean): OnboardingGateState => {
  const user = useAppSelector((state) => state.auth.user);

  // Fast-path: a user we've already seen finish onboarding skips straight to the
  // app with no status round-trip (and no loader flash) on every subsequent boot.
  const alreadyCompleted = enabled && isOnboardingCompletedCached(user?.email);

  // Whether this is a strongly-flagged brand-new user — only used to decide the
  // error fallback (fail closed for them, fail open for everyone else).
  const flaggedCandidate = enabled && isOnboardingCandidate(user);

  // Everyone else gets checked. The identity key re-runs the check when the
  // signed-in user changes.
  const candidateKey =
    enabled && !alreadyCompleted
      ? [
          user?.userID || "",
          normalizedIdentity(user?.email),
          normalizedIdentity(user?.firstName),
          normalizedIdentity(user?.lastName),
        ].join(":")
      : null;

  if (localStorageProvider.get("onbDebug") === "true") {
    // Opt-in diagnostic (set localStorage.onbDebug = "true").
    // eslint-disable-next-line no-console
    console.info("[OnboardingGate] check", {
      enabled,
      userEmail: user?.email,
      alreadyCompleted,
      flaggedCandidate,
      willCheckBackend: Boolean(candidateKey),
    });
  }

  const [snapshot, setSnapshot] = useState<OnboardingGateSnapshot>(() => ({
    candidateKey,
    state: candidateKey ? "checking" : "app",
  }));

  // A newly authenticated candidate must return "checking" during the same
  // render that enables the gate. Waiting for the effect below would allow the
  // authenticated router to mount for one render before onboarding blocks it.
  const state =
    snapshot.candidateKey === candidateKey
      ? snapshot.state
      : candidateKey
      ? "checking"
      : "app";

  useEffect(() => {
    const handleCompleted = () => setSnapshot({ candidateKey, state: "app" });
    window.addEventListener(ONBOARDING_COMPLETED_EVENT, handleCompleted);
    return () =>
      window.removeEventListener(ONBOARDING_COMPLETED_EVENT, handleCompleted);
  }, [candidateKey]);

  useEffect(() => {
    let active = true;

    if (!candidateKey) {
      setSnapshot((current) =>
        current.candidateKey === null && current.state === "app"
          ? current
          : { candidateKey: null, state: "app" }
      );
      return;
    }

    setSnapshot({ candidateKey, state: "checking" });
    (async () => {
      const result = await liveOnboardingController.getState();
      if (!active) return;

      if (localStorageProvider.get("onbDebug") === "true") {
        // Opt-in diagnostic (set localStorage.onbDebug = "true"): shows exactly
        // why a flagged user is (or isn't) routed into onboarding.
        // eslint-disable-next-line no-console
        console.info("[OnboardingGate] getState", {
          success: result.success,
          status: result.data?.status,
          error: result.error?.message,
        });
      }

      if (!result.success) {
        // Couldn't read status. Fail open for the general population so a backend
        // outage never traps the whole user base (the check re-runs next load);
        // fail closed for a strongly-flagged brand-new user so a transient error
        // can't skip their required onboarding.
        setSnapshot({
          candidateKey,
          state: flaggedCandidate ? "onboard" : "app",
        });
        return;
      }

      if (result.data?.status === "completed") {
        markOnboardingCompletedLocally(user?.email);
        clearOnboardingPendingFor(user?.email);
        notifyOnboardingCompleted();
        setSnapshot({ candidateKey, state: "app" });
        return;
      }

      setSnapshot({ candidateKey, state: "onboard" });
    })();

    return () => {
      active = false;
    };
  }, [candidateKey]);

  return state;
};
